/**
 * Catalyst Tracker store using Zustand.
 *
 * Manages state for the Catalyst Tracker page with:
 * - FSM-based job status (Kill Rule #4)
 * - Catalyst CRUD operations
 * - Real-time trigger notifications
 * - Pro/Free tier handling
 *
 * Catalysts monitor market events using AI-powered polling
 * and alert users when events occur.
 */
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import api from '@/lib/api';

// ============================================================================
// Types
// ============================================================================

export type CatalystStatus =
  | 'MONITORING'
  | 'TRIGGERED'
  | 'EXPIRED'
  | 'PAUSED'
  | 'DISMISSED';

export type PollFrequency = '15min' | '30min' | '1hr' | '4hr' | 'daily';

export type LoadingStatus = 'idle' | 'loading' | 'success' | 'error';

export interface TradeRecommendationSummary {
  id: number;
  symbol: string;
  underlying: string;
  action: string;
  instrument_type: string;
  strike: number | null;
  expiration: string | null;
  confidence_tier: string;
  confidence_score: number;
  rationale: string | null;
}

export interface Catalyst {
  id: number;
  user_id: number;
  symbol: string | null;
  description: string;
  category: string | null;
  start_date: string;
  end_date: string;
  poll_frequency: PollFrequency;
  status: CatalystStatus;
  last_checked_at: string | null;
  next_check_at: string | null;
  check_count: number;
  override_24x7: boolean;
  use_pro_model: boolean;
  triggered_at: string | null;
  trigger_confidence: number | null;
  trigger_summary: string | null;
  trigger_sources: string[] | null;
  trade_recommendation_id: number | null;
  trade_recommendation: TradeRecommendationSummary | null;
  email_sent_at: string | null;
  notification_read: boolean;
  trade_executed: boolean;
  user_action: string | null;
  user_action_at: string | null;
  is_active: boolean;
  is_triggered: boolean;
  has_trade: boolean;
  days_until_end: number;
  created_at: string;
  updated_at: string | null;
}

export interface CatalystStats {
  total: number;
  monitoring: number;
  triggered: number;
  expired: number;
  paused: number;
  unread_triggers: number;
}

export interface CreateCatalystInput {
  symbol?: string;
  description: string;
  category?: string;
  start_date: string;
  end_date: string;
  poll_frequency: PollFrequency;
  trade_recommendation_id?: number;
  override_24x7?: boolean;
  use_pro_model?: boolean;
}

export interface UpdateCatalystInput {
  status?: CatalystStatus;
  poll_frequency?: PollFrequency;
  description?: string;
  end_date?: string;
  override_24x7?: boolean;
  use_pro_model?: boolean;
}

// Poll schedule types for smart polling settings
export interface CatalystPollSchedule {
  respect_market_hours: boolean;
  market_start_hour: number;
  market_end_hour: number;
  skip_weekends: boolean;
  default_poll_interval_minutes: number;
}

export interface CatalystPollScheduleResponse {
  schedule: CatalystPollSchedule;
  savings_estimate_pct: number;
}

export interface CatalystCheckResult {
  occurred: boolean;
  confidence: number;
  summary: string;
  sources: string[];
  key_details: {
    date?: string;
    impact?: string;
    next_steps?: string;
  } | null;
}

// ============================================================================
// Poll Frequency Options
// ============================================================================

export const POLL_FREQUENCY_OPTIONS: Array<{
  value: PollFrequency;
  label: string;
  proOnly: boolean;
}> = [
  { value: 'daily', label: 'Daily', proOnly: false },
  { value: '4hr', label: 'Every 4 hours', proOnly: true },
  { value: '1hr', label: 'Hourly', proOnly: true },
  { value: '30min', label: 'Every 30 min', proOnly: true },
  { value: '15min', label: 'Every 15 min', proOnly: true },
];

export const CATEGORY_OPTIONS = [
  { value: 'earnings', label: 'Earnings' },
  { value: 'fda', label: 'FDA Decision' },
  { value: 'product_launch', label: 'Product Launch' },
  { value: 'macro', label: 'Macro Event' },
  { value: 'conference', label: 'Conference/Event' },
  { value: 'other', label: 'Other' },
];

// ============================================================================
// Store State
// ============================================================================

interface CatalystState {
  // Data
  catalysts: Catalyst[];
  stats: CatalystStats | null;
  loadingStatus: LoadingStatus;
  error: string | null;

  // Filters
  statusFilter: CatalystStatus | 'all';
  symbolFilter: string;

  // Modal state
  createModalOpen: boolean;
  createModalPrefill: Partial<CreateCatalystInput> | null;
  selectedCatalyst: Catalyst | null;
  detailSlideoutOpen: boolean;

  // Check state (for manual checks)
  checkingCatalystId: number | null;
  checkResult: CatalystCheckResult | null;

  // Actions
  fetchCatalysts: () => Promise<void>;
  fetchStats: () => Promise<void>;
  createCatalyst: (data: CreateCatalystInput) => Promise<Catalyst | null>;
  updateCatalyst: (id: number, data: UpdateCatalystInput) => Promise<void>;
  deleteCatalyst: (id: number) => Promise<void>;
  checkCatalyst: (id: number) => Promise<CatalystCheckResult | null>;
  markAsRead: (id: number) => Promise<void>;
  executeTrade: (id: number) => Promise<void>;

  // UI actions
  setStatusFilter: (status: CatalystStatus | 'all') => void;
  setSymbolFilter: (symbol: string) => void;
  openCreateModal: (prefill?: Partial<CreateCatalystInput>) => void;
  closeCreateModal: () => void;
  selectCatalyst: (catalyst: Catalyst | null) => void;
  openDetailSlideout: (catalyst: Catalyst) => void;
  closeDetailSlideout: () => void;
  clearCheckResult: () => void;

  // Triggered notification helpers
  handleTriggerNotification: (data: {
    catalyst_id: number;
    symbol: string;
    description: string;
    confidence: number;
    summary: string;
    trade_recommendation_id: number | null;
  }) => void;
}

// ============================================================================
// Store
// ============================================================================

export const useCatalystStore = create<CatalystState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        catalysts: [],
        stats: null,
        loadingStatus: 'idle',
        error: null,
        statusFilter: 'all',
        symbolFilter: '',
        createModalOpen: false,
        createModalPrefill: null,
        selectedCatalyst: null,
        detailSlideoutOpen: false,
        checkingCatalystId: null,
        checkResult: null,

        // Fetch catalysts from API
        fetchCatalysts: async () => {
          const { statusFilter, symbolFilter } = get();
          set({ loadingStatus: 'loading', error: null });

          try {
            const params = new URLSearchParams();
            if (statusFilter !== 'all') {
              params.set('status', statusFilter);
            }
            if (symbolFilter) {
              params.set('symbol', symbolFilter);
            }
            params.set('limit', '100');

            const response = await api.get(`/api/v1/catalysts?${params.toString()}`);
            set({
              catalysts: response.data.catalysts,
              loadingStatus: 'success',
            });
          } catch (error) {
            console.error('Failed to fetch catalysts:', error);
            const axiosError = error as { response?: { data?: { detail?: string } } };
            set({
              loadingStatus: 'error',
              error: axiosError.response?.data?.detail || 'Failed to fetch catalysts',
            });
          }
        },

        // Fetch stats
        fetchStats: async () => {
          try {
            const response = await api.get('/api/v1/catalysts/stats');
            set({ stats: response.data });
          } catch (error) {
            console.error('Failed to fetch catalyst stats:', error);
            const axiosError = error as { response?: { data?: { detail?: string } } };
            const message = axiosError.response?.data?.detail || 'Failed to fetch catalyst stats';
            set({ error: message });
          }
        },

        // Create new catalyst
        createCatalyst: async (data: CreateCatalystInput) => {
          try {
            const response = await api.post('/api/v1/catalysts', data);
            const newCatalyst = response.data;

            // Add to list
            set((state) => ({
              catalysts: [newCatalyst, ...state.catalysts],
              createModalOpen: false,
              createModalPrefill: null,
            }));

            // Refresh stats
            get().fetchStats();

            return newCatalyst;
          } catch (error) {
            console.error('Failed to create catalyst:', error);
            const axiosError = error as { response?: { data?: { detail?: string } } };
            throw new Error(axiosError.response?.data?.detail || 'Failed to create catalyst');
          }
        },

        // Update catalyst
        updateCatalyst: async (id: number, data: UpdateCatalystInput) => {
          try {
            const response = await api.patch(`/api/v1/catalysts/${id}`, data);
            const updatedCatalyst = response.data;

            // Update in list
            set((state) => ({
              catalysts: state.catalysts.map((c) =>
                c.id === id ? updatedCatalyst : c
              ),
              selectedCatalyst:
                state.selectedCatalyst?.id === id
                  ? updatedCatalyst
                  : state.selectedCatalyst,
            }));

            // Refresh stats
            get().fetchStats();
          } catch (error) {
            console.error('Failed to update catalyst:', error);
            const axiosError = error as { response?: { data?: { detail?: string } } };
            throw new Error(axiosError.response?.data?.detail || 'Failed to update catalyst');
          }
        },

        // Delete catalyst
        deleteCatalyst: async (id: number) => {
          try {
            await api.delete(`/api/v1/catalysts/${id}`);

            // Remove from list
            set((state) => ({
              catalysts: state.catalysts.filter((c) => c.id !== id),
              selectedCatalyst:
                state.selectedCatalyst?.id === id ? null : state.selectedCatalyst,
              detailSlideoutOpen:
                state.selectedCatalyst?.id === id ? false : state.detailSlideoutOpen,
            }));

            // Refresh stats
            get().fetchStats();
          } catch (error) {
            console.error('Failed to delete catalyst:', error);
            const axiosError = error as { response?: { data?: { detail?: string } } };
            throw new Error(axiosError.response?.data?.detail || 'Failed to delete catalyst');
          }
        },

        // Manual check
        checkCatalyst: async (id: number) => {
          set({ checkingCatalystId: id, checkResult: null });

          try {
            const response = await api.post(`/api/v1/catalysts/${id}/check`);
            const result = response.data;

            set({ checkResult: result, checkingCatalystId: null });

            // Refresh catalyst list to get updated status
            get().fetchCatalysts();
            get().fetchStats();

            return result;
          } catch (error) {
            console.error('Failed to check catalyst:', error);
            set({ checkingCatalystId: null });
            const axiosError = error as { response?: { data?: { detail?: string } } };
            throw new Error(axiosError.response?.data?.detail || 'Failed to check catalyst');
          }
        },

        // Mark as read
        markAsRead: async (id: number) => {
          try {
            await api.post(`/api/v1/catalysts/${id}/read`);

            // Update in list
            set((state) => ({
              catalysts: state.catalysts.map((c) =>
                c.id === id ? { ...c, notification_read: true } : c
              ),
            }));

            // Refresh stats
            get().fetchStats();
          } catch (error) {
            console.error('Failed to mark catalyst as read:', error);
          }
        },

        // Execute linked trade
        executeTrade: async (id: number) => {
          try {
            await api.post(`/api/v1/catalysts/${id}/execute`);

            // Update in list
            set((state) => ({
              catalysts: state.catalysts.map((c) =>
                c.id === id ? { ...c, trade_executed: true, user_action: 'executed' } : c
              ),
            }));
          } catch (error) {
            console.error('Failed to execute trade:', error);
            const axiosError = error as { response?: { data?: { detail?: string } } };
            throw new Error(axiosError.response?.data?.detail || 'Failed to execute trade');
          }
        },

        // UI actions
        setStatusFilter: (status) => {
          set({ statusFilter: status });
          get().fetchCatalysts();
        },

        setSymbolFilter: (symbol) => {
          set({ symbolFilter: symbol });
          // Debounce this in the component
        },

        openCreateModal: (prefill) => {
          set({
            createModalOpen: true,
            createModalPrefill: prefill || null,
          });
        },

        closeCreateModal: () => {
          set({
            createModalOpen: false,
            createModalPrefill: null,
          });
        },

        selectCatalyst: (catalyst) => {
          set({ selectedCatalyst: catalyst });
        },

        openDetailSlideout: (catalyst) => {
          set({
            selectedCatalyst: catalyst,
            detailSlideoutOpen: true,
          });
        },

        closeDetailSlideout: () => {
          set({
            detailSlideoutOpen: false,
          });
        },

        clearCheckResult: () => {
          set({ checkResult: null });
        },

        // Handle real-time trigger notification
        handleTriggerNotification: (data) => {
          // Update the catalyst in the list
          set((state) => ({
            catalysts: state.catalysts.map((c) =>
              c.id === data.catalyst_id
                ? {
                    ...c,
                    status: 'TRIGGERED' as CatalystStatus,
                    triggered_at: new Date().toISOString(),
                    trigger_confidence: data.confidence,
                    trigger_summary: data.summary,
                    is_triggered: true,
                    is_active: false,
                  }
                : c
            ),
          }));

          // Refresh stats
          get().fetchStats();
        },
      }),
      {
        name: 'catalyst-store',
        // Only persist filters, not data
        partialize: (state) => ({
          statusFilter: state.statusFilter,
        }),
      }
    ),
    { name: 'CatalystStore' }
  )
);

// ============================================================================
// Selectors
// ============================================================================

export const selectMonitoringCatalysts = (state: CatalystState) =>
  state.catalysts.filter((c) => c.status === 'MONITORING');

export const selectTriggeredCatalysts = (state: CatalystState) =>
  state.catalysts.filter((c) => c.status === 'TRIGGERED');

export const selectUnreadTriggeredCount = (state: CatalystState) =>
  state.catalysts.filter((c) => c.status === 'TRIGGERED' && !c.notification_read).length;
